import {useQuery} from '@apollo/client';
import CircularProgress from '@material-ui/core/CircularProgress';
import {GeneratedRound, RoundMetaData} from 'packages/common-types/base-types';
import {
  GetStandingsData,
  GetStandingsVars,
} from 'packages/common-types/queries-types';
import StyledButton from 'packages/common-ui/StyledButton';
import {ERROR_WORKER, SUCCESS_TOAST} from 'packages/constants/constants';
import ConfirmNextRound from 'packages/dialogs/ConfirmNextRound';
import {GET_STANDING} from 'packages/graphql/v2/queries';
import useAppDispatch from 'packages/hooks/use-app-dispatch';
import useAppSelector from 'packages/hooks/use-app-selector';
import {useModal} from 'packages/hooks/use-modal';
import {memo, useCallback, useEffect, useState} from 'react';
import {selectTournament, triggerToast} from 'redux/features/app-slice';
import {
  selectRoundMetadata,
  setGeneratedRound,
  setRoundMetadata,
} from 'redux/features/round-slice';
import {runWorker} from 'services/worker';
import TournamentEnd from 'packages/dialogs/TournamentEnd';

type Props = {
  renderFinished?: boolean;
};

const MoveNextRoundButton = (props: Props) => {
  const {renderFinished} = props;
  const {toggle, modalOpen} = useModal();
  const {toggle: toggleRestriccion, modalOpen: openRestriccion} = useModal();
  const dispatch = useAppDispatch();
  const selectedTournament = useAppSelector(selectTournament);
  const {currentRound} = useAppSelector(selectRoundMetadata);
  const [loading, setLoading] = useState(false);
  const [errorWorker, setErrorWorker] = useState(null);
  const {refetch} = useQuery<GetStandingsData, GetStandingsVars>(GET_STANDING, {
    skip: true,
  });

  useEffect(() => {
    if (errorWorker) {
      dispatch(triggerToast(ERROR_WORKER));
    }
  }, [errorWorker, dispatch]);

  const handleMoveToNextRound = useCallback(async () => {
    const {_id: tournamentId, conditions, hasFinal} = selectedTournament || {};
    if (tournamentId) {
      try {
        setLoading(true);
        const {data} = await refetch({tournamentId}); // Fetch data from standings...
        if (data) {
          const competitors = data.getStanding.table;
          // Run the worker if data is present
          const {index, tables: generatedTables} = await runWorker({
            modalityGame: conditions.modalityGame,
            currentRound,
            tables: competitors,
            hasFinal,
            numberOfRounds: conditions.numberOfRounds,
            restriccion: false,
          });
          const newRound: GeneratedRound = {
            index,
            tables: generatedTables,
          };

          const newMetaKeys: RoundMetaData = {
            idTournament: tournamentId,
            currentRound: index,
            nextRound: index + 1,
            saved: false,
          };
          dispatch(setRoundMetadata(newMetaKeys));
          dispatch(setGeneratedRound(newRound));
          dispatch(triggerToast(SUCCESS_TOAST));
          toggle();
        }
      } catch (error) {
        console.error(`This is the error ===> `, error);
        setErrorWorker(error);
      } finally {
        setLoading(false);
      }
    }
  }, [refetch, selectedTournament, dispatch, currentRound, toggle]);

  const handleMoveToNextRoundRestriccion = useCallback(async () => {
    const {_id: tournamentId, conditions, hasFinal} = selectedTournament || {};
    if (tournamentId) {
      try {
        setLoading(true);
        const {data} = await refetch({tournamentId}); // Fetch data from standings...
        if (data) {
          const competitors = data.getStanding.table;
          // Run the worker if data is present
          const {index, tables: generatedTables} = await runWorker({
            modalityGame: conditions.modalityGame,
            currentRound,
            tables: competitors,
            hasFinal,
            numberOfRounds: conditions.numberOfRounds,
            restriccion: true,
          });
          const newRound: GeneratedRound = {
            index,
            tables: generatedTables,
          };

          const newMetaKeys: RoundMetaData = {
            idTournament: tournamentId,
            currentRound: index,
            nextRound: index + 1,
            saved: false,
          };
          dispatch(setRoundMetadata(newMetaKeys));
          dispatch(setGeneratedRound(newRound));
          dispatch(triggerToast(SUCCESS_TOAST));
          toggleRestriccion();
        }
      } catch (error) {
        console.error(`This is the error ===> `, error);
        setErrorWorker(error);
      } finally {
        setLoading(false);
      }
    }
  }, [refetch, selectedTournament, dispatch, currentRound, toggleRestriccion]);
  return (
    <>
      {!renderFinished && (
        <ConfirmNextRound
          onSaveAndConfirm={handleMoveToNextRound}
          toggle={toggle}
          modalOpen={modalOpen}
        />
      )}
      {!renderFinished && (
        <ConfirmNextRound
          onSaveAndConfirm={handleMoveToNextRoundRestriccion}
          toggle={toggleRestriccion}
          modalOpen={openRestriccion}
        />
      )}
      {renderFinished && (
        <TournamentEnd toggle={toggle} modalOpen={modalOpen} />
      )}
      <StyledButton style={{marginLeft: 10}} disableElevation onClick={toggle}>
        {loading ? <CircularProgress /> : 'Generar Ronda'}
      </StyledButton>
      <StyledButton
        style={{marginLeft: 10}}
        disableElevation
        onClick={() => {
          toggleRestriccion();
        }}
      >
        {loading ? <CircularProgress /> : 'Generar Ronda con restriccion'}
      </StyledButton>
    </>
  );
};

const MoveNextRound = memo(MoveNextRoundButton);

MoveNextRound.displayName = 'MoveNextRound';
MoveNextRoundButton.defaultProps = {
  renderFinished: false,
};

export default MoveNextRound;
